/**
 * Form Permissions Service
 * Handles form template permission checks via backend API
 */

import { API_BASE_URL, API_ENDPOINTS } from '../config/api'

const API_BASE = API_BASE_URL

/**
 * Get access token from localStorage
 * @returns {string|null} Access token
 */
const getAccessToken = () => {
  return localStorage.getItem('aps_access_token')
}

/**
 * Get form template permissions (users and groups)
 * @param {string} projectId - Project ID
 * @param {string} templateId - Template ID
 * @returns {Promise<object>} Template permissions
 */
export async function getTemplatePermissions(projectId, templateId) {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  try {
    const response = await fetch(
      `${API_BASE}/api/form-permissions/${projectId}/templates/${templateId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(error.error || `Failed to fetch template permissions (${response.status})`)
    }

    return await response.json()
  } catch (error) {
    console.error('[Form Permissions] Failed to fetch template permissions:', error)
    throw error
  }
}

/**
 * Get user's groups in a project
 * @param {string} projectId - Project ID
 * @param {string} userId - Optional user ID
 * @returns {Promise<object>} User groups
 */
export async function getUserGroups(projectId, userId = null) {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  try {
    const url = userId
      ? `${API_BASE}/api/form-permissions/${projectId}/user-groups?userId=${userId}`
      : `${API_BASE}/api/form-permissions/${projectId}/user-groups`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(error.error || `Failed to fetch user groups (${response.status})`)
    }

    return await response.json()
  } catch (error) {
    console.error('[Form Permissions] Failed to fetch user groups:', error)
    throw error
  }
}

/**
 * Check user's access to a form template (comprehensive check)
 * Uses the backend's /api/form-permissions/:projectId/check-access endpoint
 * @param {string} projectId - Project ID
 * @param {string} templateId - Template ID (if null, checks project-level admin access)
 * @param {string} userId - Optional user ID
 * @returns {Promise<object>} Access levels and permissions
 */
export async function checkFormAccess(projectId, templateId, userId = null) {
  const token = getAccessToken()

  if (!token) {
    console.error('[Form Permissions] No access token available')
    // Return denied access if no token (fail closed)
    return {
      success: false,
      hasAccess: false,
      hasReviewerAccess: false,
      permissions: [],
      accessLevels: {
        canView: false,
        canEdit: false,
        canApprove: false,
        canReject: false,
      },
      error: 'No access token available. Please log in.',
    }
  }

  try {
    // If no templateId, use the auth/user-role endpoint for project-level check
    if (!templateId) {
      const response = await fetch(
        `${API_BASE}/api/auth/user-role?projectId=${projectId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      )

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(error.error || error.message || `Permission check failed (${response.status})`)
      }

      const roleData = await response.json()

      // Transform backend response to match expected format
      const hasReviewerAccess = roleData.hasReviewRole === true || roleData.isProjectAdmin === true

      return {
        success: roleData.success,
        hasAccess: hasReviewerAccess,
        hasReviewerAccess: hasReviewerAccess,
        isProjectAdmin: roleData.isProjectAdmin,
        permissions: hasReviewerAccess ? ['review', 'approve'] : [],
        accessLevels: {
          canView: hasReviewerAccess,
          canEdit: roleData.isProjectAdmin === true,
          canApprove: hasReviewerAccess,
          canReject: hasReviewerAccess,
        },
        roleIds: roleData.roleIds || [],
        role: roleData.isProjectAdmin ? 'admin' : (hasReviewerAccess ? 'reviewer' : 'viewer'),
        email: roleData.email,
        status: roleData.status,
      }
    }

    // For template-specific permissions, use the form-permissions endpoint
    const response = await fetch(
      `${API_BASE}/api/form-permissions/${projectId}/check-access`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          templateId,
          userId,
        }),
      }
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(error.error || error.message || `Permission check failed (${response.status})`)
    }

    const data = await response.json()

    // Backend returns: { success, hasAccess, hasReviewerAccess, permissions, accessLevels, userId, userEmail }
    // The backend uses a simple permission model where ANY permission grants full access
    return {
      success: data.success,
      hasAccess: data.hasAccess,
      hasReviewerAccess: data.hasReviewerAccess,
      isProjectAdmin: data.permissions?.includes('manage') && data.accessLevels?.canEdit === true,
      permissions: data.permissions || [],
      accessLevels: {
        canView: data.accessLevels?.canView ?? true,
        canEdit: data.accessLevels?.canEdit ?? false,
        canApprove: data.accessLevels?.canApprove ?? false,
        canReject: data.accessLevels?.canReject ?? false,
      },
      roleIds: [], // Template-level permissions don't use roleIds
      role: data.hasReviewerAccess ? 'reviewer' : 'viewer',
      email: data.userEmail,
    }
  } catch (error) {
    console.error('[Form Permissions] Failed to check form access:', error)
    // Return denied access on error (fail closed)
    return {
      success: false,
      hasAccess: false,
      hasReviewerAccess: false,
      permissions: [],
      accessLevels: {
        canView: false,
        canEdit: false,
        canApprove: false,
        canReject: false,
      },
      error: error.message,
    }
  }
}

/**
 * Cache for form permissions to avoid repeated API calls
 */
const permissionsCache = new Map()
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

/**
 * Get cached form access or fetch if not cached
 * @param {string} projectId - Project ID
 * @param {string} templateId - Template ID
 * @returns {Promise<object>} Access levels and permissions
 */
export async function getCachedFormAccess(projectId, templateId) {
  const cacheKey = `${projectId}:${templateId}`
  const cached = permissionsCache.get(cacheKey)

  // Return cached if still valid
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data
  }

  // Fetch fresh permissions
  const data = await checkFormAccess(projectId, templateId)

  // Cache the result
  permissionsCache.set(cacheKey, {
    data,
    timestamp: Date.now(),
  })

  return data
}

/**
 * Clear permissions cache (call this after permission changes)
 */
export function clearPermissionsCache() {
  permissionsCache.clear()
}

/**
 * Clear cache for a specific template
 * @param {string} projectId - Project ID
 * @param {string} templateId - Template ID
 */
export function clearTemplateCache(projectId, templateId) {
  const cacheKey = `${projectId}:${templateId}`
  permissionsCache.delete(cacheKey)
}

export default {
  getTemplatePermissions,
  getUserGroups,
  checkFormAccess,
  getCachedFormAccess,
  clearPermissionsCache,
  clearTemplateCache,
}
